import { useEffect, useState } from "react";
import { ProductDetails } from "types/ProductCatalogue";
import { fetch as fetchVats } from "features/vats/vats.service";
import { fetchById } from "./products.service";

type Answer = { productId: string; product: ProductDetails | null };

/**
 * The open product, loaded in full. Another product's answer is never shown;
 * after `reload` the old figures stay until the new ones arrive.
 * `product` is null while loading, and `isFailed` says the load did not work.
 */
export function useProductDetails(companyId: string, productId: string) {
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // False once the product changes or the page closes, so a late answer is dropped.
    let isCurrent = true;
    fetchById(companyId, productId)
      .then(({ productEntity }: { productEntity: ProductDetails }) => {
        if (isCurrent) setAnswer({ productId, product: productEntity });
      })
      .catch(() => {
        if (isCurrent) setAnswer({ productId, product: null });
      });
    return () => {
      isCurrent = false;
    };
  }, [companyId, productId, attempt]);

  const ofThisProduct = answer && answer.productId === productId ? answer : null;
  return {
    product: ofThisProduct ? ofThisProduct.product : null,
    isFailed: !!ofThisProduct && ofThisProduct.product === null,
    reload: () => setAttempt((count) => count + 1),
  };
}

/** VAT rate id -> percent, for the company's rates; empty until loaded (the product holds only the id). */
export function useVatPercents(): Record<string, number> {
  const [percents, setPercents] = useState<Record<string, number>>({});

  useEffect(() => {
    let isCurrent = true;
    fetchVats()
      .then(({ vatsCollection }: { vatsCollection: { id: string; percent: number }[] }) => {
        if (!isCurrent) return;
        const byId: Record<string, number> = {};
        for (const vat of vatsCollection) byId[vat.id] = vat.percent;
        setPercents(byId);
      })
      .catch(() => {
        // The VAT field of the header stays blank; nothing else depends on it.
      });
    return () => {
      isCurrent = false;
    };
  }, []);

  return percents;
}
